import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { Observable, ReplaySubject, Subscription } from 'rxjs';

import { ButtonEventsService } from './services/button-events.service';
import { CmxLayoutService } from './services/cmx-layout.service';
import { LegalEntityService } from './services/legal-entity.service';
import { MenuService } from './services/menu.service';
import { Direction } from './types/directions';
import { IFooterLink } from './types/footerLink';
import { IHeaderMenu } from './types/headerMenu';
import { ILanguage } from './types/language';
import { ILegalEntity } from './types/legalEntity';
import { ApplicationMenu } from './types/menu';
import { IMobileLink } from './types/mobileLink';
import { IConsolesMenu } from './types/consoles';
import { IAppMenuItem } from './types/appMenu';
import { NotificationsService } from './services/notifications.service';
import { Notification } from './types/notification';
import { map, mergeMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'cmx-layout-container',
  template: `
    <cmx-layout
      class="{{ variantHeader }}"
      [employeeVariant]="employeeVariant"
      [entityId]="entityId"
      [entityName]="entityName"
    >
      <ng-content select="[cmx-logo]" cmx-logo></ng-content>
      <ng-content></ng-content>
    </cmx-layout>
  `,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./styles.scss'],
})
// tslint:disable-next-line:component-class-suffix
export class CmxLayoutContainer implements OnInit, OnDestroy, OnChanges {
  // Functional events
  @Output()
  logout = new EventEmitter<void>();
  @Output()
  languageChange = new EventEmitter<string | number>();
  @Output()
  legalEntitySearchChange = new EventEmitter<string>();
  @Output()
  legalEntityChange = new EventEmitter<number>();
  @Output()
  legalEntityReachedEnd = new EventEmitter<void>();
  @Output()
  isLayoutRendered: EventEmitter<boolean> = new EventEmitter();

  // Links events
  @Output()
  footerLinkClick = new EventEmitter<number | string>();
  @Output()
  mobileLinkClick = new EventEmitter<number | string>();
  @Output()
  userClick = new EventEmitter<void>();
  @Output()
  logoClick = new EventEmitter<void>();
  @Output()
  menuClick = new EventEmitter<number>();
  @Output()
  headerMenuItemClick = new EventEmitter<number | string>();
  @Output()
  feedbackClick = new EventEmitter<void>();
  @Output()
  footerLabelClick = new EventEmitter<void>();
  @Output()
  chatboxClick = new EventEmitter<void>();
  @Output()
  consolesMenuClick = new EventEmitter<string | number>();
  @Output()
  appMenuClick = new EventEmitter<string | number>();
  @Output()
  notificationsViewAllClick = new EventEmitter<void>();
  @Output()
  notificationsDropdownOpen = new EventEmitter<boolean>();
  @Output()
  notificationItemClick = new EventEmitter<Notification>();
  @Output()
  entityModalOpen: EventEmitter<boolean> = new EventEmitter();

  // Layout inputs
  // Set the breakpoint for mobile version in Pixels
  @Input()
  mobileBreakpoint = 768;
  // Wheater app is in mobile mode or not(based on mobile breakpoint)
  @Output()
  mobile = new EventEmitter<boolean>();
  // Set the direction of the website
  @Input()
  direction: Direction;
  // Set the header background to green
  @Input()
  green: boolean;

  // Feedback
  @Input()
  feedback: boolean;
  @Input()
  feedbackLabel: string;

  // Chatbox
  @Input()
  chatbox: boolean;
  @Input()
  chatboxLabel: string;

  // Notifications inputs
  // Should display notifications
  @Input()
  notificationsEnabled: boolean;

  @Input()
  notifications: Notification[] = [];

  @Input()
  notificationsLabel: string;

  @Input()
  notificationsViewAllLabel: string;

  @Input()
  notificationsUnavailableLabel: string;

  // Notification count displayed in circular icon
  @Input()
  notificationsCount: number;

  @Input()
  notificationsLoadingLabel: string;
  @Input()
  notificationsDelayLabel: string;
  @Input()
  notificationsErrorLabel: string;
  @Input()
  notificationsErrorLinkRefresh: string;
  @Input()
  noNotificationsLabel: string;
  @Input()
  showUserNotifications: boolean; // if empty: empty message
  @Input()
  userNotificationsFirstLoad: boolean; // have notifications been loaded for the first time?
  @Input()
  userNotificationLoading: boolean;

  // User details inputs
  @Input()
  userInitials: string;
  @Input()
  userName: string;
  @Input()
  userEmail: string;
  @Input()
  userCountry: string;
  @Input()
  logoutLabel: string;
  @Input()
  myProfileLabel: string;

  // Consoles menu
  @Input()
  consolesMenu: boolean;
  @Input()
  consolesMenuItems: IConsolesMenu[];

  // Languages inputs
  @Input()
  language: boolean;
  @Input()
  languages: ILanguage[] = [];
  @Input()
  languageMobileLabel: string;
  @Input()
  activeLanguageId: number;
  @Input()
  languageLabel: string;

  // Legal Entity inputs
  @Input()
  legalEntity: boolean;
  @Input()
  legalEntityPlaceholder: string;
  @Input()
  legalEntityMobileLabel: string;
  @Input()
  legalEntityMessage: string;
  @Input()
  legalEntityLoading: boolean;
  @Input()
  legalEntityList: ILegalEntity[];
  @Input()
  legalEntityActive: ILegalEntity;

  // Menu Items
  @Input()
  menuItems: ApplicationMenu[];

  // Header Menu Items
  @Input()
  headerMenuItems: IHeaderMenu[];

  // Footer Links
  @Input()
  footerLinks: IFooterLink[];
  @Input()
  footerLabel: string;

  // Mobile menu links
  @Input()
  mobileLinks: IMobileLink[];

  // Label Inputs
  @Input()
  applyLabel: string;
  @Input()
  cancelLabel: string;

  // Loader
  @Input()
  loader: number;

  // Development direction control
  @Input()
  directionSwitcher: boolean;
  @Output()
  directionSelect: EventEmitter<Direction> = new EventEmitter();

  // Apps Menu
  @Input()
  appMenu = false;
  @Input()
  appMenuLabel: string;
  @Input()
  appMenuItems: IAppMenuItem[];

  // Version of the sidebar and nav header
  @Input()
  version: number;

  @Input()
  needHelpLabel: string;
  @Input()
  ServiceCenterApp: string;
  @Input()
  feedBackTitle: string;
  @Input()
  contactLabel: string;
  @Input()
  developerCenterLabel: string;
  @Input()
  settingsLabel: string;
  @Input()
  supportContact: IHeaderMenu;
  @Input()
  disabledProfile: boolean;
  @Input()
  variantHeader: string;
  @Input()
  brandLogo: string;

  // Entity Selector modal
  @Input()
  modalLabelApply: string;
  @Input()
  modalLabelCancel: string;
  @Input()
  modalLabelChoose: string;
  @Input()
  modalLabelEmptyHeader: string;
  @Input()
  modalLabelEmptyMessage: string;
  @Input()
  modalLabelFilter: string;
  @Input()
  modalTitle: string;
  @Input()
  entityName: string = null;
  @Input()
  entityId: string = null;
  @Input()
  employeeVariant = false;
  @Input()
  chatbotLabel: string;

  private subscriptions: Subscription[] = [];
  private isMobile: boolean;
  private layoutRendered$: Observable<boolean>;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private layout: CmxLayoutService,
    private buttons: ButtonEventsService,
    private legalEntities: LegalEntityService,
    private notificationService: NotificationsService,
    private menu: MenuService,
  ) {}

  ngOnInit() {
    this.detectMobile();
    this.subscriptions.push(
      // Connect Functional events outputs
      this.layout.languageChange.subscribe((value) => this.languageChange.emit(value)),
      this.legalEntities.searchTermChange.subscribe((value) => this.legalEntitySearchChange.emit(value)),
      this.legalEntities.legalEntityChange.subscribe((value) => this.legalEntityChange.emit(value)),
      this.legalEntities.legalEntityReachedEnd.subscribe(() => this.legalEntityReachedEnd.emit()),
      this.menu.menuClick.subscribe((value) => this.menuClick.emit(value)),
      // Connect Link events outputs
      this.layout.footerLinkClick.subscribe((value) => this.footerLinkClick.emit(value)),
      this.layout.mobileLinkClick.subscribe((value) => this.mobileLinkClick.emit(value)),
      this.layout.headerMenuItemClick.subscribe((value) => this.headerMenuItemClick.emit(value)),
      this.layout.feedbackClick.subscribe(() => this.feedbackClick.emit()),
      this.buttons.logout.subscribe(() => this.logout.emit()),
      this.buttons.logo.subscribe(() => this.logoClick.emit()),
      this.buttons.user.subscribe(() => this.userClick.emit()),
      this.notificationService.viewAllNotifications.subscribe(() => this.notificationsViewAllClick.emit()),
      this.notificationService.notificationsDropdownOpen.subscribe((isOpen) =>
        this.notificationsDropdownOpen.emit(isOpen),
      ),
      this.notificationService.notificationItemClick.subscribe((notification) =>
        this.notificationItemClick.emit(notification),
      ),
      this.layout.footerLabelClick.subscribe(() => this.footerLabelClick.emit()),
      this.layout.chatboxClick.subscribe(() => this.chatboxClick.emit()),
      this.layout.consolesMenuClick.subscribe((value) => this.consolesMenuClick.emit(value)),
      this.layout.isMobile.subscribe((value) => {
        this.isMobile = value;
        this.mobile.emit(value);
      }),
      this.layout.directionSelect.subscribe((value) => this.directionSelect.emit(value)),
      this.layout.appMenuClick.subscribe((value) => this.appMenuClick.emit(value)),
    );
    this.layoutRendered$ = this.layout.sideBarMenuRendered.pipe(
      mergeMap((sideBarMenuRendered) =>
        this.layout.headerRendered.pipe(map((headerRendered) => sideBarMenuRendered && headerRendered)),
      ),
    );
    this.layoutRendered$.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
      if (data) {
        this.isLayoutRendered.emit(data);
      }
    });
    this.legalEntities.isModalOpen.pipe(takeUntil(this.destroyed$)).subscribe((isOpen) => {
      this.entityModalOpen.emit(isOpen);
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.direction && changes.direction.previousValue !== changes.direction.currentValue) {
      this.layout.direction.next(this.direction);
    }
    if (changes.userInitials && changes.userInitials.previousValue !== changes.userInitials.currentValue) {
      this.layout.userInitials.next(this.userInitials);
    }
    if (changes.userName && changes.userName.previousValue !== changes.userName.currentValue) {
      this.layout.userName.next(this.userName);
    }
    if (changes.userEmail && changes.userEmail.previousValue !== changes.userEmail.currentValue) {
      this.layout.userEmail.next(this.userEmail);
    }
    if (changes.userCountry && changes.userCountry.previousValue !== changes.userCountry.currentValue) {
      this.layout.userCountry.next(this.userCountry);
    }
    if (changes.feedback && changes.feedback.previousValue !== changes.feedback.currentValue) {
      this.layout.feedback.next(this.feedback);
    }
    if (changes.feedbackLabel && changes.feedbackLabel.previousValue !== changes.feedbackLabel.currentValue) {
      this.layout.feedbackLabel.next(this.feedbackLabel);
    }
    if (changes.needHelpLabel && changes.needHelpLabel.previousValue !== changes.needHelpLabel.currentValue) {
      this.layout.needHelpLabel.next(this.needHelpLabel);
    }
    if (changes.ServiceCenterApp && changes.ServiceCenterApp.previousValue !== changes.ServiceCenterApp.currentValue) {
      this.layout.ServiceCenterApp.next(this.ServiceCenterApp);
    }
    if (changes.feedBackTitle && changes.feedBackTitle.previousValue !== changes.feedBackTitle.currentValue) {
      this.layout.feedBackTitle.next(this.feedBackTitle);
    }
    if (changes.contactLabel && changes.contactLabel.previousValue !== changes.contactLabel.currentValue) {
      this.layout.contactLabel.next(this.contactLabel);
    }
    if (
      changes.developerCenterLabel &&
      changes.developerCenterLabel.previousValue !== changes.developerCenterLabel.currentValue
    ) {
      this.layout.developerCenterLabel.next(this.developerCenterLabel);
    }
    if (changes.settingsLabel && changes.settingsLabel.previousValue !== changes.settingsLabel.currentValue) {
      this.layout.settingsLabel.next(this.settingsLabel);
    }
    if (changes.supportContact && changes.supportContact.previousValue !== changes.supportContact.currentValue) {
      this.layout.supportContact.next(this.supportContact);
    }

    if (changes.chatbox && changes.chatbox.previousValue !== changes.chatbox.currentValue) {
      this.layout.chatbox.next(this.chatbox);
    }
    if (changes.chatboxLabel && changes.chatboxLabel.previousValue !== changes.chatboxLabel.currentValue) {
      this.layout.chatboxLabel.next(this.chatboxLabel);
    }
    if (changes.notifications && changes.notifications.previousValue !== changes.notifications.currentValue) {
      this.notificationService.notificationsEnabled.next(this.notificationsEnabled);
    }
    if (
      changes.notificationsLabel &&
      changes.notificationsLabel.previousValue !== changes.notificationsLabel.currentValue
    ) {
      this.notificationService.notificationsLabel.next(this.notificationsLabel);
    }
    if (
      changes.notificationsViewAllLabel &&
      changes.notificationsViewAllLabel.previousValue !== changes.notificationsViewAllLabel.currentValue
    ) {
      this.notificationService.notificationsViewAllLabel.next(this.notificationsViewAllLabel);
    }
    if (
      changes.notificationsUnavailableLabel &&
      changes.notificationsUnavailableLabel.previousValue !== changes.notificationsUnavailableLabel.currentValue
    ) {
      this.notificationService.notificationsUnavailableLabel.next(this.notificationsUnavailableLabel);
    }
    if (
      changes.notificationsCount &&
      changes.notificationsCount.previousValue !== changes.notificationsCount.currentValue
    ) {
      this.notificationService.notificationsCount.next(this.notificationsCount);
    }
    if (changes.notifications && changes.notifications.previousValue !== changes.notifications.currentValue) {
      this.notificationService.notifications.next(this.notifications);
    }
    if (
      changes.notificationsLoadingLabel &&
      changes.notificationsLoadingLabel.previousValue !== changes.notificationsLoadingLabel.currentValue
    ) {
      this.notificationService.notificationsLoadingLabel.next(this.notificationsLoadingLabel);
    }
    if (
      changes.notificationsDelayLabel &&
      changes.notificationsDelayLabel.previousValue !== changes.notificationsDelayLabel.currentValue
    ) {
      this.notificationService.notificationsDelayLabel.next(this.notificationsDelayLabel);
    }
    if (
      changes.notificationsErrorLabel &&
      changes.notificationsErrorLabel.previousValue !== changes.notificationsErrorLabel.currentValue
    ) {
      this.notificationService.notificationsErrorLabel.next(this.notificationsErrorLabel);
    }
    if (
      changes.notificationsErrorLinkRefresh &&
      changes.notificationsErrorLinkRefresh.previousValue !== changes.notificationsErrorLinkRefresh.currentValue
    ) {
      this.notificationService.notificationsErrorLinkRefresh.next(this.notificationsErrorLinkRefresh);
    }
    if (
      changes.noNotificationsLabel &&
      changes.noNotificationsLabel.previousValue !== changes.noNotificationsLabel.currentValue
    ) {
      this.notificationService.noNotificationsLabel.next(this.noNotificationsLabel);
    }
    if (
      changes.showUserNotifications &&
      changes.showUserNotifications.previousValue !== changes.showUserNotifications.currentValue
    ) {
      this.notificationService.showUserNotifications.next(this.showUserNotifications);
    }
    if (
      changes.userNotificationsFirstLoad &&
      changes.userNotificationsFirstLoad.previousValue !== changes.userNotificationsFirstLoad.currentValue
    ) {
      this.notificationService.userNotificationsFirstLoad.next(this.userNotificationsFirstLoad);
    }
    if (
      changes.userNotificationLoading &&
      changes.userNotificationLoading.previousValue !== changes.userNotificationLoading.currentValue
    ) {
      this.notificationService.userNotificationLoading.next(this.userNotificationLoading);
    }

    // Language change
    if (changes.language && changes.language.previousValue !== changes.language.currentValue) {
      this.layout.language.next(this.language);
    }
    if (changes.languageLabel && changes.languageLabel.previousValue !== changes.languageLabel.currentValue) {
      this.layout.languageLabel.next(this.languageLabel);
    }
    if (
      changes.languageMobileLabel &&
      changes.languageMobileLabel.previousValue !== changes.languageMobileLabel.currentValue
    ) {
      this.layout.languageMobileLabel.next(this.languageMobileLabel);
    }
    if (changes.activeLanguageId && changes.activeLanguageId.previousValue !== changes.activeLanguageId.currentValue) {
      this.layout.activeLanguageId.next(this.activeLanguageId);
    }
    if (changes.languages && changes.languages.previousValue !== changes.languages.currentValue) {
      this.layout.languages.next(this.languages);
    }
    if (changes.logoutLabel && changes.logoutLabel.previousValue !== changes.logoutLabel.currentValue) {
      this.buttons.logoutLabel.next(this.logoutLabel);
    }
    if (changes.myProfileLabel && changes.myProfileLabel.previousValue !== changes.myProfileLabel.currentValue) {
      this.buttons.myProfileLabel.next(this.myProfileLabel);
    }
    if (changes.legalEntity && changes.legalEntity.previousValue !== changes.legalEntity.currentValue) {
      this.legalEntities.legalEntity.next(this.legalEntity);
    }
    if (
      changes.legalEntityMessage &&
      changes.legalEntityMessage.previousValue !== changes.legalEntityMessage.currentValue
    ) {
      this.legalEntities.message.next(this.legalEntityMessage);
    }
    if (
      changes.legalEntityPlaceholder &&
      changes.legalEntityPlaceholder.previousValue !== changes.legalEntityPlaceholder.currentValue
    ) {
      this.legalEntities.searchPlaceholder.next(this.legalEntityPlaceholder);
    }
    if (
      changes.legalEntityLoading &&
      changes.legalEntityLoading.previousValue !== changes.legalEntityLoading.currentValue
    ) {
      this.legalEntities.isLoading.next(this.legalEntityLoading);
    }
    if (changes.legalEntityList && changes.legalEntityList.previousValue !== changes.legalEntityList.currentValue) {
      this.legalEntities.legalEntitiesList.next(this.legalEntityList);
    }
    if (
      changes.legalEntityActive &&
      changes.legalEntityActive.previousValue !== changes.legalEntityActive.currentValue
    ) {
      this.legalEntities.legalEntityActive.next(this.legalEntityActive);
    }
    if (
      changes.legalEntityMobileLabel &&
      changes.legalEntityMobileLabel.previousValue !== changes.legalEntityMobileLabel.currentValue
    ) {
      this.legalEntities.legalEntityMobileLabel.next(this.legalEntityMobileLabel);
    }
    if (changes.menuItems && changes.menuItems.previousValue !== changes.menuItems.currentValue) {
      this.menu.menuItems.next(this.menuItems);
    }
    if (
      changes.consolesMenuItems &&
      changes.consolesMenuItems.previousValue !== changes.consolesMenuItems.currentValue
    ) {
      this.layout.consolesMenuItems.next(this.consolesMenuItems);
    }
    if (changes.consolesMenu && changes.consolesMenu.previousValue !== changes.consolesMenu.currentValue) {
      this.layout.consolesMenu.next(this.consolesMenu);
    }
    if (changes.footerLabel && changes.footerLabel.previousValue !== changes.footerLabel.currentValue) {
      this.layout.footerLabel.next(this.footerLabel);
    }
    if (changes.footerLinks && changes.footerLinks.previousValue !== changes.footerLinks.currentValue) {
      this.layout.footerLinks.next(this.footerLinks);
    }
    if (changes.applyLabel && changes.applyLabel.previousValue !== changes.applyLabel.currentValue) {
      this.buttons.applyLabel.next(this.applyLabel);
    }
    if (changes.cancelLabel && changes.cancelLabel.previousValue !== changes.cancelLabel.currentValue) {
      this.buttons.cancelLabel.next(this.cancelLabel);
    }
    if (changes.mobileLinks && changes.mobileLinks.previousValue !== changes.mobileLinks.currentValue) {
      this.layout.mobileLinks.next(this.mobileLinks);
    }
    if (changes.headerMenuItems && changes.headerMenuItems.previousValue !== changes.headerMenuItems.currentValue) {
      this.layout.headerMenuItems.next(this.headerMenuItems);
    }
    if (changes.loader && changes.loader.previousValue !== changes.loader.currentValue) {
      this.layout.loader.next(this.loader);
    }
    if (changes.green && changes.green.previousValue !== changes.green.currentValue) {
      this.layout.isGreen.next(this.green);
    }
    if (
      changes.directionSwitcher &&
      changes.directionSwitcher.previousValue !== changes.directionSwitcher.currentValue
    ) {
      this.layout.directionSwitcher.next(this.directionSwitcher);
    }
    if (changes.appMenu && changes.appMenu.previousValue !== changes.appMenu.currentValue) {
      this.layout.appMenu.next(this.appMenu);
    }
    if (changes.appMenuLabel && changes.appMenuLabel.previousValue !== changes.appMenuLabel.currentValue) {
      this.layout.appMenuLabel.next(this.appMenuLabel);
    }
    if (changes.appMenuItems && changes.appMenuItems.previousValue !== changes.appMenuItems.currentValue) {
      this.layout.appMenuItems.next(this.appMenuItems);
    }
    if (changes.version && changes.version.previousValue !== changes.version.currentValue) {
      this.layout.version.next(this.version);
    }
    if (changes.variantHeader && changes.variantHeader.previousValue !== changes.variantHeader.currentValue) {
      this.layout.variantHeader.next(this.variantHeader);
    }
    if (changes.disabledProfile && changes.disabledProfile.previousValue !== changes.disabledProfile.currentValue) {
      this.layout.disabledProfile.next(this.disabledProfile);
    }
    if (changes.brandLogo && changes.brandLogo.previousValue !== changes.brandLogo.currentValue) {
      this.layout.brandLogo.next(this.brandLogo);
    }
    if (changes.modalLabelApply && changes.modalLabelApply.previousValue !== changes.modalLabelApply.currentValue) {
      this.layout.modalLabelApply.next(this.modalLabelApply);
    }
    if (changes.modalLabelCancel && changes.modalLabelCancel.previousValue !== changes.modalLabelCancel.currentValue) {
      this.layout.modalLabelCancel.next(this.modalLabelCancel);
    }
    if (changes.modalLabelChoose && changes.modalLabelChoose.previousValue !== changes.modalLabelChoose.currentValue) {
      this.layout.modalLabelChoose.next(this.modalLabelChoose);
    }
    if (
      changes.modalLabelEmptyHeader &&
      changes.modalLabelEmptyHeader.previousValue !== changes.modalLabelEmptyHeader.currentValue
    ) {
      this.layout.modalLabelEmptyHeader.next(this.modalLabelEmptyHeader);
    }
    if (
      changes.modalLabelEmptyMessage &&
      changes.modalLabelEmptyMessage.previousValue !== changes.modalLabelEmptyMessage.currentValue
    ) {
      this.layout.modalLabelEmptyMessage.next(this.modalLabelEmptyMessage);
    }
    if (changes.modalLabelFilter && changes.modalLabelFilter.previousValue !== changes.modalLabelFilter.currentValue) {
      this.layout.modalLabelFilter.next(this.modalLabelFilter);
    }
    if (changes.modalTitle && changes.modalTitle.previousValue !== changes.modalTitle.currentValue) {
      this.layout.modalTitle.next(this.modalTitle);
    }
    if (changes.chatbotLabel && changes.chatbotLabel.previousValue !== changes.chatbotLabel.currentValue) {
      this.layout.chatbotLabel.next(this.chatbotLabel);
    }
  }

  @HostListener('window:resize', [])
  onresize() {
    this.detectMobile();
  }

  private detectMobile() {
    if (window.innerWidth <= this.mobileBreakpoint) {
      this.layout.isCollapsed.next(false);
      !this.isMobile && this.layout.isMobile.next(true);
    } else {
      this.isMobile && this.layout.isMobile.next(false);
    }
  }

  ngOnDestroy() {
    this.subscriptions.map((sub) => sub.unsubscribe());
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
